import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { mdiDotsGrid } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dropdown, Tooltip } from "antd";
import { observer } from "mobx-react-lite";

import ngdataIcon from "../../../assets/img/menu-icon-ngdata.svg";
import ngidIcon from "../../../assets/img/menu-icon-ngid.svg";
import qmsIcon from "../../../assets/img/menu-icon-qms.svg";
import toolboxIcon from "../../../assets/img/menu-icon-toolbox.svg";
import webgisIcon from "../../../assets/img/menu-icon-webgis.svg";
import { userStore } from "../../../stores/userStore";
import { sendCounterEvent } from "../../../utils/counterService";

import "./ServicesMenu.css";

const EXTERNAL_SERVICES = [
  {
    key: "data",
    icon: ngdataIcon,
    href: "https://data.nextgis.com/login/?next=/",
  },
  {
    key: "toolbox",
    icon: toolboxIcon,
    href: "https://toolbox.nextgis.com/login/?next=/",
  },
  {
    key: "qms",
    icon: qmsIcon,
    href: "https://qms.nextgis.com/login/?next=/",
  },
] as const;

const ServiceItemContent = ({
  icon,
  title,
  desc,
}: {
  icon: string;
  title: string;
  desc: string;
}) => (
  <>
    <img src={icon} width={22} height={22} alt="" />
    <div className="services-menu__item-text">
      <span className="services-menu__item-title">{title}</span>
      <span className="services-menu__item-desc">{desc}</span>
    </div>
  </>
);

export const ServicesMenu = observer(function ServicesMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleItemClick = (eventName: string) => {
    sendCounterEvent(eventName);
    setOpen(false);
  };

  const webgis = userStore.webgis;
  const webgisHref = webgis
    ? `https://${webgis.subdomain_name}.nextgis.com/oauth?na=next`
    : null;

  const overlay = (
    <div className="services-menu__dropdown">
      <Link
        key="ngid"
        className="services-menu__item"
        to="/profile"
        onClick={() => handleItemClick("menu_id")}
      >
        <ServiceItemContent
          icon={ngidIcon}
          title={t("services.ngid.title")}
          desc={t("services.ngid.desc")}
        />
      </Link>

      {webgisHref ? (
        <a
          key="webgis"
          className="services-menu__item"
          href={webgisHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleItemClick("menu_webgis")}
        >
          <ServiceItemContent
            icon={webgisIcon}
            title={t("services.webgis.title")}
            desc={t("services.webgis.desc")}
          />
        </a>
      ) : (
        <Link
          key="webgis"
          className="services-menu__item"
          to="/webgis/create"
          onClick={() => handleItemClick("menu_create_webgis")}
        >
          <ServiceItemContent
            icon={webgisIcon}
            title={t("services.webgis.createTitle")}
            desc={t("services.webgis.desc")}
          />
        </Link>
      )}

      {EXTERNAL_SERVICES.map(({ key, icon, href }) => (
        <a
          key={key}
          className="services-menu__item"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleItemClick(`menu_${key}`)}
        >
          <ServiceItemContent
            icon={icon}
            title={t(`services.${key}.title`)}
            desc={t(`services.${key}.desc`)}
          />
        </a>
      ))}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => overlay}
      placement="bottomRight"
      align={{ offset: [0, 14] }}
      trigger={["click"]}
    >
      <Tooltip
        title={t("header.servicesTooltip")}
        open={open ? false : undefined}
      >
        <Button
          type="text"
          className="app-header__services-btn"
          style={{ backgroundColor: "transparent" }}
          icon={<Icon color="#aec8e0" path={mdiDotsGrid} size={1} />}
        />
      </Tooltip>
    </Dropdown>
  );
});
